import { FunctionComponent, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FlightIcon from "@mui/icons-material/Flight";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import SouthEastIcon from "@mui/icons-material/SouthEast";
import { useQueryClient } from "react-query";

import { API_URL } from "../../config";
import { invalidateNavigationSummary } from "../../hooks/useNavigationSummary";
import { useLeonFlightSnapshot } from "../../hooks/useLeonTransferDelay";
import {
  buildLeonPickerOptions,
  calculateDurationMinutes,
  convertUpliftMassToVolume,
  convertUpliftVolumeToMass,
  formatDurationMinutes,
  formatNumberInput,
  formatUtcDateTimeInput,
  LEON_UTC_INPUT_FORMAT,
  parseUtcDateTimeInput,
} from "./leonEditDialogUtils";
import { LeonJourneyLogReviewPane } from "./LeonJourneyLogReviewPane";

const toBrowserLocalDateTimeValue = (value?: string | null) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return "";
  const pad = (part: number) => `${part}`.padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const fromBrowserLocalDateTimeValue = (value: string) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf())) return null;
  return parsed.toISOString();
};

const normalizeNullable = (value?: string | null) => {
  if (value === null || value === undefined) return null;
  const normalized = `${value}`.trim();
  return normalized.length ? normalized : null;
};

const parseNullableNumber = (raw: string): number | null => {
  if (raw === "") return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
};

const parseBoolSelectValue = (value: string) => {
  if (value === "") return null;
  return value === "true";
};

const parseApproachDaySelectValue = (value: string) => {
  if (value === "") return null;
  return value === "day";
};

const boolToSelectValue = (value?: boolean | null) => {
  if (value === null || value === undefined) return "";
  return value ? "true" : "false";
};

const approachDayToSelectValue = (value?: boolean | null) => {
  if (value === null || value === undefined) return "";
  return value ? "day" : "night";
};

let localRowCounter = 0;
const nextLocalKey = (prefix: string) => `${prefix}-${Date.now()}-${localRowCounter++}`;

type EditableApproachRow = LeonJourneyLogApproachRow & { clientKey: string };
type EditableDelayRow = LeonJourneyLogDelayRow & { clientKey: string };
type EditableDeicingRow = LeonJourneyLogDeicingRow & { clientKey: string };
type UpliftEditSource = "mass" | "volume" | null;

const createEmptyApproachRow = (): EditableApproachRow => ({
  clientKey: nextLocalKey("approach"),
  id: null,
  type: null,
  successful: null,
  day: null,
  EFVS: null,
});

const createEmptyDelayRow = (): EditableDelayRow => ({
  clientKey: nextLocalKey("delay"),
  id: null,
  minutes: null,
  reason: null,
  remark: null,
});

const createEmptyDeicingRow = (): EditableDeicingRow => ({
  clientKey: nextLocalKey("deicing"),
  id: null,
  deicing_type: null,
  deicing_mix: null,
  deicing_timestamp: null,
  qty: null,
  brand: null,
});

const withApproachKeys = (rows: LeonJourneyLogApproachRow[] = []) =>
  rows.length
    ? rows.map((row) => ({ ...row, clientKey: nextLocalKey("approach") }))
    : [createEmptyApproachRow()];

const withDelayKeys = (rows: LeonJourneyLogDelayRow[] = []) =>
  rows.length
    ? rows.map((row) => ({ ...row, clientKey: nextLocalKey("delay") }))
    : [createEmptyDelayRow()];

const withDeicingKeys = (rows: LeonJourneyLogDeicingRow[] = []) =>
  rows.length
    ? rows.map((row) => ({ ...row, clientKey: nextLocalKey("deicing") }))
    : [createEmptyDeicingRow()];

const formatCrewOptionLabel = (
  crewOptions: LeonJourneyLogCrewOption[] = [],
  crewId?: number | null,
) => {
  if (!crewId) return "—";
  const crew = crewOptions.find((entry) => entry.id === crewId);
  if (!crew) return `Crew ${crewId}`;
  return [crew.tlc, crew.rank, crew.position].filter(Boolean).join(" · ") || `Crew ${crewId}`;
};

const buildEditValues = (values?: Partial<LeonEditableSnapshot["values"]> | null) => ({
  out: formatUtcDateTimeInput(values?.out),
  off: formatUtcDateTimeInput(values?.off),
  on: formatUtcDateTimeInput(values?.on),
  in: formatUtcDateTimeInput(values?.in),
  uplift: values?.uplift != null ? `${values.uplift}` : "",
  fuelBeforeUplift: values?.fuelBeforeUplift != null ? `${values.fuelBeforeUplift}` : "",
  fuelBeforeUpliftMass:
    values?.fuelBeforeUpliftMass != null ? `${values.fuelBeforeUpliftMass}` : "",
  fuelAfterUpliftMass:
    values?.fuelAfterUpliftMass != null ? `${values.fuelAfterUpliftMass}` : "",
  offblockFuel: values?.offblockFuel != null ? `${values.offblockFuel}` : "",
  onblockFuel: values?.onblockFuel != null ? `${values.onblockFuel}` : "",
  density: values?.density != null ? `${values.density}` : "",
  burnedFuel: values?.burnedFuel != null ? `${values.burnedFuel}` : "",
  reason: "",
});

const timeFieldDefinitions = [
  { key: "out", label: "Off-Block", icon: AccessTimeIcon },
  { key: "off", label: "Take-OFF", icon: NorthEastIcon },
  { key: "on", label: "Landing", icon: SouthEastIcon },
  { key: "in", label: "On-Block", icon: AccessTimeIcon },
] as const;

type Props = {
  open: boolean;
  flightId: string | null;
  flightLabel?: string | null;
  editSource?: string;
  initialTab?: "times" | "fuel" | "fuel-uplift" | "jl-data";
  initialReviewMode?: boolean;
  onClose: () => void;
  onSaved?: (result: {
    flightId: string;
    regeneratePdf: boolean;
    correctedPdfVersion?: LeonPdfVersion | null;
    summaryResendSuggestion?: LeonSummaryResendSuggestion | null;
  }) => void;
};

export const LeonTransferEditDialog: FunctionComponent<Props> = ({
  open,
  flightId,
  flightLabel,
  editSource = "ops",
  initialTab = "times",
  initialReviewMode = true,
  onClose,
  onSaved,
}) => {
  const queryClient = useQueryClient();
  const [editTab, setEditTab] = useState(initialTab);
  const [reviewMode, setReviewMode] = useState(initialReviewMode);
  const { data: snapshotData, isFetching: isLoadingSnapshot, isError: isSnapshotError } =
    useLeonFlightSnapshot(open && flightId ? flightId : null);
  const [editValues, setEditValues] = useState(() =>
    buildEditValues(open ? snapshotData?.values : null),
  );
  const [dirtyEditFields, setDirtyEditFields] = useState<Record<string, boolean>>({});
  const [approachRows, setApproachRows] = useState<EditableApproachRow[]>([createEmptyApproachRow()]);
  const [delayRows, setDelayRows] = useState<EditableDelayRow[]>([createEmptyDelayRow()]);
  const [deicingRows, setDeicingRows] = useState<EditableDeicingRow[]>([createEmptyDeicingRow()]);
  const [crewRoles, setCrewRoles] = useState({
    picCrewId: null as number | null,
    takeoffCrewId: null as number | null,
    landingCrewId: null as number | null,
  });
  const [dirtyJourneyLogSections, setDirtyJourneyLogSections] = useState<Record<string, boolean>>({});
  const [lastUpliftEditSource, setLastUpliftEditSource] = useState<UpliftEditSource>(null);
  const [regeneratePdf, setRegeneratePdf] = useState(true);
  const [editError, setEditError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const massFuelUnitLabel = useMemo(() => {
    const unit = (
      snapshotData?.units?.massFuelUnit ||
      snapshotData?.fuelUnit ||
      "lb"
    ).toUpperCase();
    return unit;
  }, [snapshotData?.fuelUnit, snapshotData?.units?.massFuelUnit]);

  const upliftVolumeUnitLabel = useMemo(() => {
    const unit = snapshotData?.units?.upliftVolumeUnit || "L";
    return `${unit}`.toUpperCase();
  }, [snapshotData?.units?.upliftVolumeUnit]);

  const densityUnitLabel = useMemo(() => {
    const unit = snapshotData?.units?.densityUnit;
    return unit ? `${unit}` : "kg/L";
  }, [snapshotData?.units?.densityUnit]);

  const approachTypeOptions = useMemo(
    () =>
      buildLeonPickerOptions(
        snapshotData?.options?.approachTypes || [],
        approachRows.map((row) => row.type),
      ),
    [approachRows, snapshotData?.options?.approachTypes],
  );

  const deicingTypeOptions = useMemo(
    () =>
      buildLeonPickerOptions(
        snapshotData?.options?.deicingTypes || [],
        deicingRows.map((row) => row.deicing_type),
      ),
    [deicingRows, snapshotData?.options?.deicingTypes],
  );

  const delayHelperText = useMemo(() => {
    if (snapshotData?.options?.delayReasons?.length) {
      return `Existing reasons: ${snapshotData.options.delayReasons.join(", ")}`;
    }
    return "No existing delay reasons for this flight yet.";
  }, [snapshotData?.options?.delayReasons]);

  const flightTimeDisplay = useMemo(() => {
    const offIso = parseUtcDateTimeInput(editValues.off);
    const onIso = parseUtcDateTimeInput(editValues.on);
    return formatDurationMinutes(calculateDurationMinutes(offIso, onIso));
  }, [editValues.off, editValues.on]);

  const blockTimeDisplay = useMemo(() => {
    const outIso = parseUtcDateTimeInput(editValues.out);
    const inIso = parseUtcDateTimeInput(editValues.in);
    return formatDurationMinutes(calculateDurationMinutes(outIso, inIso));
  }, [editValues.in, editValues.out]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setEditTab(initialTab);
    setReviewMode(initialReviewMode);
    setEditValues(buildEditValues(null));
    setDirtyEditFields({});
    setApproachRows([createEmptyApproachRow()]);
    setDelayRows([createEmptyDelayRow()]);
    setDeicingRows([createEmptyDeicingRow()]);
    setCrewRoles({ picCrewId: null, takeoffCrewId: null, landingCrewId: null });
    setDirtyJourneyLogSections({});
    setLastUpliftEditSource(null);
    setRegeneratePdf(true);
    setEditError("");
  }, [open, flightId, initialTab, initialReviewMode]);

  useEffect(() => {
    if (!snapshotData?.values || !open) return;
    const values = snapshotData.values;
    setEditValues(buildEditValues(values));
    setLastUpliftEditSource(null);
    setApproachRows(withApproachKeys(snapshotData.journeyLog?.approaches || []));
    setDelayRows(withDelayKeys(snapshotData.journeyLog?.delays || []));
    setDeicingRows(withDeicingKeys(snapshotData.journeyLog?.deicings || []));
    setCrewRoles({
      picCrewId: snapshotData.journeyLog?.crewRoles?.picCrewId || null,
      takeoffCrewId: snapshotData.journeyLog?.crewRoles?.takeoffCrewId || null,
      landingCrewId: snapshotData.journeyLog?.crewRoles?.landingCrewId || null,
    });
  }, [snapshotData, open]);

  const markJourneyLogDirty = (section: string) => {
    setDirtyJourneyLogSections((current) => ({ ...current, [section]: true }));
    setEditError("");
  };

  const handleDialogClose = () => {
    if (isSaving) return;
    onClose();
  };

  const applyFuelUpliftEdit = (
    current: typeof editValues,
    field: string,
    value: string,
  ) => {
    const next = { ...current, [field]: value };
    const conversionContext = {
      density: next.density,
      densityUnit: densityUnitLabel,
      massFuelUnit: massFuelUnitLabel,
    };

    if (field === "uplift") {
      const liters = convertUpliftMassToVolume({
        mass: value,
        ...conversionContext,
      });
      next.fuelBeforeUplift = formatNumberInput(liters, 2);
    }

    if (field === "fuelBeforeUplift") {
      const mass = convertUpliftVolumeToMass({
        liters: value,
        ...conversionContext,
      });
      next.uplift = formatNumberInput(
        mass,
        massFuelUnitLabel.toLowerCase() === "kg" ? 0 : 2,
      );
    }

    if (field === "density") {
      if (lastUpliftEditSource === "volume") {
        const mass = convertUpliftVolumeToMass({
          liters: next.fuelBeforeUplift,
          ...conversionContext,
        });
        next.uplift = formatNumberInput(
          mass,
          massFuelUnitLabel.toLowerCase() === "kg" ? 0 : 2,
        );
      } else {
        const liters = convertUpliftMassToVolume({
          mass: next.uplift,
          ...conversionContext,
        });
        next.fuelBeforeUplift = formatNumberInput(liters, 2);
      }
    }

    if (field === "uplift" || field === "fuelBeforeUpliftMass") {
      const before = Number(next.fuelBeforeUpliftMass);
      const uplift = Number(next.uplift);
      if (
        next.fuelAfterUpliftMass === "" &&
        Number.isFinite(before) &&
        Number.isFinite(uplift)
      ) {
        next.fuelAfterUpliftMass = formatNumberInput(
          before + uplift,
          massFuelUnitLabel.toLowerCase() === "kg" ? 0 : 2,
        );
      }
    }

    return next;
  };

  const handleEditChange = (field: string, value: string) => {
    setEditValues((current) => {
      const isFuelUpliftField = [
        "uplift",
        "fuelBeforeUplift",
        "fuelBeforeUpliftMass",
        "density",
      ].includes(field);
      const next =
        isFuelUpliftField
          ? applyFuelUpliftEdit(current, field, value)
          : { ...current, [field]: value };
      if (field === "offblockFuel" || field === "onblockFuel") {
        const offblock = Number(next.offblockFuel);
        const onblock = Number(next.onblockFuel);
        next.burnedFuel =
          Number.isFinite(offblock) && Number.isFinite(onblock)
            ? `${Math.max(offblock - onblock, 0)}`
            : "";
      }
      return next;
    });
    if (field === "uplift") {
      setLastUpliftEditSource("mass");
    }
    if (field === "fuelBeforeUplift") {
      setLastUpliftEditSource("volume");
    }
    if (field !== "reason" && field !== "burnedFuel") {
      setDirtyEditFields((current) => ({ ...current, [field]: true }));
    }
    setEditError("");
  };

  const handleApproachChange = (
    clientKey: string,
    field: keyof LeonJourneyLogApproachRow,
    value: string | boolean | null,
  ) => {
    setApproachRows((current) =>
      current.map((row) =>
        row.clientKey === clientKey ? { ...row, [field]: value } : row,
      ),
    );
    markJourneyLogDirty("approaches");
  };

  const handleDelayChange = (
    clientKey: string,
    field: keyof LeonJourneyLogDelayRow,
    value: string | number | null,
  ) => {
    setDelayRows((current) =>
      current.map((row) =>
        row.clientKey === clientKey ? { ...row, [field]: value } : row,
      ),
    );
    markJourneyLogDirty("delays");
  };

  const handleDeicingChange = (
    clientKey: string,
    field: keyof LeonJourneyLogDeicingRow,
    value: string | number | null,
  ) => {
    setDeicingRows((current) =>
      current.map((row) =>
        row.clientKey === clientKey ? { ...row, [field]: value } : row,
      ),
    );
    markJourneyLogDirty("deicings");
  };

  const addApproachRow = () => {
    setApproachRows((current) => [...current, createEmptyApproachRow()]);
    markJourneyLogDirty("approaches");
  };

  const addDelayRow = () => {
    setDelayRows((current) => [...current, createEmptyDelayRow()]);
    markJourneyLogDirty("delays");
  };

  const addDeicingRow = () => {
    setDeicingRows((current) => [...current, createEmptyDeicingRow()]);
    markJourneyLogDirty("deicings");
  };

  const removeApproachRow = (clientKey: string) => {
    setApproachRows((current) => {
      const next = current.filter((row) => row.clientKey !== clientKey);
      return next.length ? next : [createEmptyApproachRow()];
    });
    markJourneyLogDirty("approaches");
  };

  const removeDelayRow = (clientKey: string) => {
    setDelayRows((current) => {
      const next = current.filter((row) => row.clientKey !== clientKey);
      return next.length ? next : [createEmptyDelayRow()];
    });
    markJourneyLogDirty("delays");
  };

  const removeDeicingRow = (clientKey: string) => {
    setDeicingRows((current) => {
      const next = current.filter((row) => row.clientKey !== clientKey);
      return next.length ? next : [createEmptyDeicingRow()];
    });
    markJourneyLogDirty("deicings");
  };

  const handleCrewRoleChange = (
    field: "picCrewId" | "takeoffCrewId" | "landingCrewId",
    value: string,
  ) => {
    const parsed = value === "" ? null : Number(value);
    setCrewRoles((current) => ({ ...current, [field]: Number.isFinite(parsed) ? parsed : null }));
    markJourneyLogDirty("crewRoles");
  };

  const buildJourneyLogPayload = () => {
    const payload: Record<string, unknown> = {};

    if (dirtyJourneyLogSections.approaches) {
      payload.approaches = approachRows.map(({ clientKey, ...row }) => row);
    }

    if (dirtyJourneyLogSections.delays) {
      payload.delays = delayRows.map(({ clientKey, ...row }) => row);
    }

    if (dirtyJourneyLogSections.deicings) {
      payload.deicings = deicingRows.map(({ clientKey, ...row }) => row);
    }

    if (dirtyJourneyLogSections.crewRoles) {
      payload.crewRoles = crewRoles;
    }

    return Object.keys(payload).length ? payload : null;
  };

  const handleSubmitEdit = async () => {
    if (!flightId) return;

    const reason = editValues.reason.trim();
    if (!reason) {
      setEditError("Reason is required.");
      return;
    }

    const changes: Record<string, string | number | null> = {};

    const putDate = (field: "out" | "off" | "on" | "in") => {
      if (!dirtyEditFields[field]) {
        return;
      }
      const value = editValues[field];
      if (!value.length) {
        changes[field] = null;
        return;
      }
      const parsed = parseUtcDateTimeInput(value);
      if (!parsed) {
        throw new Error(
          `${field.toUpperCase()} must use ${LEON_UTC_INPUT_FORMAT} UTC`,
        );
      }
      changes[field] = parsed;
    };

    const putNum = (field: string, raw: string) => {
      if (!dirtyEditFields[field]) {
        return;
      }
      const trimmed = raw.trim();
      if (!trimmed.length) {
        changes[field] = null;
        return;
      }
      const parsed = Number(trimmed);
      if (!Number.isFinite(parsed)) {
        throw new Error(`${field} must be a valid number (got "${trimmed}")`);
      }
      changes[field] = parsed;
    };

    try {
      putDate("out");
      putDate("off");
      putDate("on");
      putDate("in");
      putNum("uplift", editValues.uplift);
      putNum("fuelBeforeUplift", editValues.fuelBeforeUplift);
      putNum("fuelBeforeUpliftMass", editValues.fuelBeforeUpliftMass);
      putNum("fuelAfterUpliftMass", editValues.fuelAfterUpliftMass);
      putNum("offblockFuel", editValues.offblockFuel);
      putNum("onblockFuel", editValues.onblockFuel);
      putNum("density", editValues.density);

      const journeyLogChanges = buildJourneyLogPayload();
      if (!Object.keys(changes).length && !journeyLogChanges) {
        setEditError("Provide at least one field to update.");
        return;
      }

      setIsSaving(true);

      const response = await fetch(`${API_URL}/ops/leon/flights/${flightId}/edit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          reason,
          changes,
          journeyLogChanges,
          editSource,
          regeneratePdf,
        }),
      });

      if (!response.ok) {
        let message = `Failed to save LEON changes (${response.status})`;
        try {
          const errorPayload = await response.json();
          if (errorPayload?.message) message = errorPayload.message;
          else if (errorPayload?.text) message = errorPayload.text;
        } catch (_error) {
          /* ignore */
        }
        setEditError(message);
        return;
      }

      const payload = await response.json().catch(() => ({}));

      queryClient.invalidateQueries(["leon-flight-snapshot", flightId]);
      invalidateNavigationSummary(queryClient);
      onClose();
      if (onSaved) {
        onSaved({
          flightId,
          regeneratePdf,
          correctedPdfVersion: payload?.correctedPdfVersion ?? null,
          summaryResendSuggestion: payload?.summaryResendSuggestion ?? null,
        });
      }
    } catch (error) {
      setEditError(error instanceof Error ? error.message : "Invalid values");
    } finally {
      setIsSaving(false);
    }
  };

  const timesFields = (
    <Stack spacing={1.5}>
      {timeFieldDefinitions.map(({ key, label, icon: Icon }) => (
        <Stack
          key={key}
          direction="row"
          spacing={1.5}
          alignItems="flex-start"
          data-testid={`leon-time-row-${key}`}
        >
          <Box
            sx={{
              pt: 2,
              minWidth: 32,
              display: "flex",
              justifyContent: "center",
              color: "text.secondary",
            }}
          >
            <Icon fontSize="small" />
          </Box>
          <TextField
            fullWidth
            label={`${label} (UTC)`}
            value={editValues[key]}
            onChange={(event) => handleEditChange(key, event.target.value)}
            placeholder={LEON_UTC_INPUT_FORMAT}
            helperText={`Format: ${LEON_UTC_INPUT_FORMAT} UTC`}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {key === "off" || key === "on" ? (
                    <FlightIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                  ) : (
                    <AccessTimeIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                  )}
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      ))}
      <Box
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 1.5,
          px: 2,
          py: 1.5,
          bgcolor: "background.default",
        }}
      >
        <Stack spacing={0.75}>
          <Typography variant="subtitle2">Calculated times</Typography>
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Flight time
            </Typography>
            <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
              {flightTimeDisplay}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Block time
            </Typography>
            <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
              {blockTimeDisplay}
            </Typography>
          </Stack>
        </Stack>
      </Box>
    </Stack>
  );

  const timesDelayFields = (
    <Box data-testid="leon-delay-fields">
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Typography variant="subtitle2">Delay</Typography>
        <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={addDelayRow}>
          Add delay
        </Button>
      </Stack>
      <Stack spacing={1}>
        {delayRows.map((row) => (
          <Stack
            key={row.clientKey}
            direction="row"
            spacing={1}
            alignItems="center"
            data-testid="leon-delay-row"
          >
            <TextField
              fullWidth
              label="Delay code"
              value={row.reason || ""}
              onChange={(event) =>
                handleDelayChange(row.clientKey, "reason", normalizeNullable(event.target.value))
              }
            />
            <TextField
              type="number"
              label="Minutes"
              value={row.minutes ?? ""}
              onChange={(event) =>
                handleDelayChange(
                  row.clientKey,
                  "minutes",
                  parseNullableNumber(event.target.value),
                )
              }
              sx={{ width: 140, flex: "0 0 140px" }}
              inputProps={{ min: 0 }}
            />
            <IconButton
              aria-label="Remove delay row"
              onClick={() => removeDelayRow(row.clientKey)}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Stack>
        ))}
      </Stack>
    </Box>
  );

  const fuelFieldDirection = reviewMode ? "column" : { xs: "column" as const, md: "row" as const };
  const jlFieldDirection = reviewMode ? "column" : { xs: "column" as const, lg: "row" as const };
  const crewFieldDirection = reviewMode ? "column" : { xs: "column" as const, md: "row" as const };
  const fuelUpliftFields = (
    <Stack spacing={1.5}>
      <Stack direction={fuelFieldDirection} spacing={1.5}>
        <TextField
          fullWidth
          type="number"
          label={`Fuel uplift (${massFuelUnitLabel})`}
          value={editValues.uplift}
          onChange={(event) => handleEditChange("uplift", event.target.value)}
          helperText="Mass uplift in the operator fuel unit."
        />
        <TextField
          fullWidth
          type="number"
          label={`Fuel uplift volume (${upliftVolumeUnitLabel})`}
          value={editValues.fuelBeforeUplift}
          onChange={(event) => handleEditChange("fuelBeforeUplift", event.target.value)}
          helperText="Volume uplift."
        />
      </Stack>
      <Stack direction={fuelFieldDirection} spacing={1.5}>
        <TextField
          fullWidth
          type="number"
          label={`Fuel before uplift (${massFuelUnitLabel})`}
          value={editValues.fuelBeforeUpliftMass}
          onChange={(event) =>
            handleEditChange("fuelBeforeUpliftMass", event.target.value)
          }
          helperText="Fuel quantity before fueling."
        />
        <TextField
          fullWidth
          type="number"
          label={`Fuel after uplift (${massFuelUnitLabel})`}
          value={editValues.fuelAfterUpliftMass}
          onChange={(event) =>
            handleEditChange("fuelAfterUpliftMass", event.target.value)
          }
          helperText="Fuel quantity after fueling."
        />
        <TextField
          fullWidth
          type="number"
          label={`Density (${densityUnitLabel})`}
          value={editValues.density}
          onChange={(event) => handleEditChange("density", event.target.value)}
          helperText="Leave blank to store no value."
        />
      </Stack>
    </Stack>
  );

  const fuelFields = (
    <Stack spacing={1.5}>
      <Stack direction={fuelFieldDirection} spacing={1.5}>
        <TextField
          fullWidth
          type="number"
          label={`Offblock fuel (${massFuelUnitLabel})`}
          value={editValues.offblockFuel}
          onChange={(event) => handleEditChange("offblockFuel", event.target.value)}
        />
        <TextField
          fullWidth
          type="number"
          label={`Onblock fuel (${massFuelUnitLabel})`}
          value={editValues.onblockFuel}
          onChange={(event) => handleEditChange("onblockFuel", event.target.value)}
        />
        <TextField
          fullWidth
          type="number"
          label={`Burned fuel (${massFuelUnitLabel})`}
          value={editValues.burnedFuel}
          InputProps={{ readOnly: true }}
          helperText="Calculated automatically (offblock - onblock)."
        />
      </Stack>
    </Stack>
  );

  const jlDataFields = (
    <Stack spacing={2}>
      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Approaches</Typography>
          <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={addApproachRow}>
            Add row
          </Button>
        </Stack>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mb: 1.25 }}
        >
          At least one approach is required. Each approach must be marked as day or night.
          If multiple approaches are entered, at least one must be successful.
        </Typography>
        <Stack spacing={1}>
          {approachRows.map((row) => (
            <Box key={row.clientKey} sx={{ p: 1.25, borderRadius: 2, border: (theme) => `1px solid ${theme.palette.divider}` }}>
              <Stack direction={jlFieldDirection} spacing={1} alignItems={reviewMode ? undefined : { lg: "center" }}>
                <TextField
                  select
                  fullWidth
                  label="Type of approach"
                  value={row.type || ""}
                  onChange={(event) => handleApproachChange(row.clientKey, "type", normalizeNullable(event.target.value))}
                >
                  <MenuItem value="">Empty</MenuItem>
                  {approachTypeOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  fullWidth
                  label="Successful"
                  value={boolToSelectValue(row.successful)}
                  onChange={(event) => handleApproachChange(row.clientKey, "successful", parseBoolSelectValue(event.target.value))}
                >
                  <MenuItem value="">Empty</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </TextField>
                <TextField
                  select
                  fullWidth
                  label="Day / Night"
                  value={approachDayToSelectValue(row.day)}
                  onChange={(event) =>
                    handleApproachChange(
                      row.clientKey,
                      "day",
                      parseApproachDaySelectValue(event.target.value),
                    )}
                >
                  <MenuItem value="">Select day or night</MenuItem>
                  <MenuItem value="day">Day</MenuItem>
                  <MenuItem value="night">Night</MenuItem>
                </TextField>
                <TextField
                  select
                  fullWidth
                  label="EFVS"
                  value={boolToSelectValue(row.EFVS)}
                  onChange={(event) => handleApproachChange(row.clientKey, "EFVS", parseBoolSelectValue(event.target.value))}
                >
                  <MenuItem value="">Empty</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </TextField>
                <IconButton aria-label="Remove approach row" onClick={() => removeApproachRow(row.clientKey)}>
                  <DeleteOutlineIcon />
                </IconButton>
              </Stack>
            </Box>
          ))}
        </Stack>
      </Box>

      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Delays</Typography>
          <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={addDelayRow}>
            Add row
          </Button>
        </Stack>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mb: 1.25 }}
        >
          {delayHelperText}
        </Typography>
        <Stack spacing={1}>
          {delayRows.map((row) => (
            <Box key={row.clientKey} sx={{ p: 1.25, borderRadius: 2, border: (theme) => `1px solid ${theme.palette.divider}` }}>
              <Stack direction={jlFieldDirection} spacing={1} alignItems={reviewMode ? undefined : { lg: "center" }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Minutes"
                  value={row.minutes ?? ""}
                  onChange={(event) => handleDelayChange(row.clientKey, "minutes", parseNullableNumber(event.target.value))}
                />
                <TextField
                  fullWidth
                  label="Delay reason"
                  value={row.reason || ""}
                  onChange={(event) => handleDelayChange(row.clientKey, "reason", normalizeNullable(event.target.value))}
                />
                <TextField
                  fullWidth
                  label="Remark"
                  value={row.remark || ""}
                  onChange={(event) => handleDelayChange(row.clientKey, "remark", normalizeNullable(event.target.value))}
                />
                <IconButton aria-label="Remove delay row" onClick={() => removeDelayRow(row.clientKey)}>
                  <DeleteOutlineIcon />
                </IconButton>
              </Stack>
            </Box>
          ))}
        </Stack>
      </Box>

      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">De-icings</Typography>
          <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={addDeicingRow}>
            Add row
          </Button>
        </Stack>
        <Stack spacing={1}>
          {deicingRows.map((row) => (
            <Box key={row.clientKey} sx={{ p: 1.25, borderRadius: 2, border: (theme) => `1px solid ${theme.palette.divider}` }}>
              <Stack direction={jlFieldDirection} spacing={1} alignItems={reviewMode ? undefined : { lg: "center" }}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Time"
                  InputLabelProps={{ shrink: true }}
                  value={toBrowserLocalDateTimeValue(row.deicing_timestamp)}
                  onChange={(event) => handleDeicingChange(row.clientKey, "deicing_timestamp", fromBrowserLocalDateTimeValue(event.target.value))}
                />
                <TextField
                  select
                  fullWidth
                  label="De-icing type"
                  value={row.deicing_type || ""}
                  onChange={(event) => handleDeicingChange(row.clientKey, "deicing_type", normalizeNullable(event.target.value))}
                >
                  <MenuItem value="">Empty</MenuItem>
                  {deicingTypeOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  fullWidth
                  type="number"
                  label="Mix"
                  value={row.deicing_mix ?? ""}
                  onChange={(event) => handleDeicingChange(row.clientKey, "deicing_mix", parseNullableNumber(event.target.value))}
                />
                <TextField
                  fullWidth
                  type="number"
                  label="Quantity"
                  value={row.qty ?? ""}
                  onChange={(event) => handleDeicingChange(row.clientKey, "qty", parseNullableNumber(event.target.value))}
                />
                <TextField
                  fullWidth
                  label="Brand"
                  value={row.brand || ""}
                  onChange={(event) => handleDeicingChange(row.clientKey, "brand", normalizeNullable(event.target.value))}
                />
                <IconButton aria-label="Remove de-icing row" onClick={() => removeDeicingRow(row.clientKey)}>
                  <DeleteOutlineIcon />
                </IconButton>
              </Stack>
            </Box>
          ))}
        </Stack>
      </Box>

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Crew roles
        </Typography>
        <Stack direction={crewFieldDirection} spacing={1.5}>
          <TextField
            select
            fullWidth
            label="PIC"
            value={crewRoles.picCrewId ?? ""}
            onChange={(event) => handleCrewRoleChange("picCrewId", event.target.value)}
          >
            <MenuItem value="">Empty</MenuItem>
            {(snapshotData?.journeyLog?.crewOptions || []).map((crew, index) => (
              <MenuItem key={`pic-${crew.id ?? index}`} value={crew.id ?? ""}>
                {formatCrewOptionLabel(snapshotData?.journeyLog?.crewOptions || [], crew.id)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            label="Take-off"
            value={crewRoles.takeoffCrewId ?? ""}
            onChange={(event) => handleCrewRoleChange("takeoffCrewId", event.target.value)}
          >
            <MenuItem value="">Empty</MenuItem>
            {(snapshotData?.journeyLog?.crewOptions || []).map((crew, index) => (
              <MenuItem key={`takeoff-${crew.id ?? index}`} value={crew.id ?? ""}>
                {formatCrewOptionLabel(snapshotData?.journeyLog?.crewOptions || [], crew.id)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            label="Landing"
            value={crewRoles.landingCrewId ?? ""}
            onChange={(event) => handleCrewRoleChange("landingCrewId", event.target.value)}
          >
            <MenuItem value="">Empty</MenuItem>
            {(snapshotData?.journeyLog?.crewOptions || []).map((crew, index) => (
              <MenuItem key={`landing-${crew.id ?? index}`} value={crew.id ?? ""}>
                {formatCrewOptionLabel(snapshotData?.journeyLog?.crewOptions || [], crew.id)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </Box>
    </Stack>
  );

  return (
    <Dialog open={open} onClose={handleDialogClose} fullWidth maxWidth={reviewMode ? "xl" : "lg"}>
      <DialogTitle>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          useFlexGap
          flexWrap="wrap"
        >
          <Typography component="span" variant="h6">
            LEON transfer edit
            {flightId ? ` · ${flightLabel || flightId}` : ""}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={reviewMode}
                onChange={(event) => setReviewMode(event.target.checked)}
                inputProps={{ "aria-label": "Review mode" }}
              />
            }
            label="Review mode"
          />
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ "&&": { pt: 2 } }}>
        {isLoadingSnapshot ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : isSnapshotError ? (
          <Typography variant="body2" color="error.main" sx={{ pt: 2 }}>
            Failed to load flight data. Please close the dialog and try again.
          </Typography>
        ) : (
          <Stack spacing={2} sx={{ pt: 1 }}>
            {reviewMode ? (
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                alignItems="stretch"
                data-testid="leon-review-layout"
              >
                <Box
                  sx={{
                    order: { xs: 2, md: 1 },
                    flex: { md: "0 0 400px" },
                    minWidth: 0,
                    maxHeight: { md: 640 },
                    overflowY: "auto",
                    overflowX: "hidden",
                    pt: 2.5,
                    pr: 2.5,
                    pb: 1,
                    boxSizing: "border-box",
                    scrollbarGutter: "stable",
                  }}
                >
                  <Stack spacing={2} sx={{ pr: 0.5 }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Times
                      </Typography>
                      {timesFields}
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Fuel
                      </Typography>
                      {fuelFields}
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Fuel-Uplift
                      </Typography>
                      {fuelUpliftFields}
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        JL-Data
                      </Typography>
                      {jlDataFields}
                    </Box>
                  </Stack>
                </Box>
                <Box
                  sx={{
                    order: { xs: 1, md: 2 },
                    flex: 1,
                    minWidth: 0,
                    minHeight: { xs: 320, md: 560 },
                  }}
                >
                  {flightId ? <LeonJourneyLogReviewPane flightId={flightId} /> : (
                    <Typography variant="body2" color="text.secondary">
                      No journey log document uploaded
                    </Typography>
                  )}
                </Box>
              </Stack>
            ) : (
              <>
            <Tabs
              value={editTab}
              onChange={(_event, value) => setEditTab(value)}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab value="times" label="Times" />
              <Tab value="fuel" label="Fuel" />
              <Tab value="fuel-uplift" label="Fuel-Uplift" />
              <Tab value="jl-data" label="JL-Data" />
            </Tabs>

            {editTab === "times" ? (
              <Stack spacing={1.5}>
                {timesFields}
                {timesDelayFields}
              </Stack>
            ) : null}

            {editTab === "fuel" ? fuelFields : null}

            {editTab === "fuel-uplift" ? fuelUpliftFields : null}

            {editTab === "jl-data" ? jlDataFields : null}

              </>
            )}

            <TextField
              required
              fullWidth
              label="Reason"
              value={editValues.reason}
              onChange={(event) => handleEditChange("reason", event.target.value)}
              helperText="Reason is mandatory for LEON transfer-related edits."
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={regeneratePdf}
                  onChange={(event) => setRegeneratePdf(event.target.checked)}
                />
              }
              label={<Typography variant="body2">Regenerate corrected PDF summary after saving</Typography>}
            />
            {editError ? (
              <Typography variant="body2" color="error.main">
                {editError}
              </Typography>
            ) : null}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDialogClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmitEdit}
          disabled={isSaving || isLoadingSnapshot || isSnapshotError || !flightId}
        >
          {isSaving ? "Saving..." : "Save changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
